import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { BookOpen, Plus, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const STATUS_TONE = { cleared: "text-emerald-600", stopped: "text-red-600", stale: "text-amber-600" };

export default function ChequeBookTab() {
  const [books, setBooks] = useState([]);
  const [form, setForm]  = useState({ bank_account_id: "", bank_key: "HDFC", series_from: "", series_to: "" });
  const [statusForm, setStatusForm] = useState({ cb_id: "", cheque_number: "", status: "cleared", reason: "" });

  const load = async () => setBooks((await api.get("/ops/cheque-books")).data.cheque_books || []);
  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      await api.post("/ops/cheque-books", { ...form, series_from: Number(form.series_from), series_to: Number(form.series_to) });
      toast.success("Cheque book added"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const nextNo = async (id) => {
    try { const r = await api.post(`/ops/cheque-books/${id}/next-number`); toast.success(`Next: ${r.data.cheque_number} · ${r.data.remaining} left`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setStatus = async () => {
    if (!statusForm.cb_id || !statusForm.cheque_number) return toast.error("Pick a book + cheque #");
    try { await api.patch(`/ops/cheque-books/${statusForm.cb_id}/status`, statusForm); toast.success(`Marked ${statusForm.status}`); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="cheque-book-tab">
      <div className="text-[10px] font-mono uppercase tracking-widest text-accent flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> Cheque Book Register</div>

      <Card className="p-4 rounded-sm border">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div><Label>Bank Account ID</Label><Input value={form.bank_account_id} onChange={e => setForm({...form, bank_account_id: e.target.value})} className="rounded-sm mt-1 h-10" data-testid="cb-account" /></div>
          <div><Label>Bank</Label>
            <Select value={form.bank_key} onValueChange={v => setForm({...form, bank_key: v})}>
              <SelectTrigger className="rounded-sm mt-1 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>{["HDFC","SBI","ICICI","AXIS","KOTAK","CUSTOM"].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Series from</Label><Input type="number" value={form.series_from} onChange={e => setForm({...form, series_from: e.target.value})} className="rounded-sm mt-1 h-10 text-right tabular-nums" /></div>
          <div><Label>Series to</Label><Input type="number" value={form.series_to} onChange={e => setForm({...form, series_to: e.target.value})} className="rounded-sm mt-1 h-10 text-right tabular-nums" /></div>
          <div className="flex items-end"><Button onClick={add} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5 h-10 w-full" data-testid="cb-add"><Plus className="w-4 h-4" />Add book</Button></div>
        </div>
      </Card>

      <Card className="rounded-sm border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] font-mono uppercase text-muted-foreground bg-muted/40">
            <tr><th className="p-2 text-left">Bank</th><th className="p-2 text-left">Account</th><th className="p-2 text-right">Series</th><th className="p-2 text-right">Next</th><th className="p-2 text-right">Remaining</th><th className="p-2 text-left">Issued / Cleared / Stopped / Stale</th><th className="p-2 text-right">Action</th></tr>
          </thead>
          <tbody>
            {books.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground text-sm">No cheque books yet.</td></tr>}
            {books.map(b => (
              <tr key={b.id} className="border-t" data-testid={`cb-row-${b.id}`}>
                <td className="p-2 font-medium">{b.bank_key}</td>
                <td className="p-2 font-mono text-[11px]">{b.bank_account_id}</td>
                <td className="p-2 text-right font-mono">{b.series_from}–{b.series_to}</td>
                <td className="p-2 text-right font-mono">{b.next_number}</td>
                <td className="p-2 text-right font-mono">{Math.max(0, b.series_to - (b.next_number - 1))}</td>
                <td className="p-2 text-[11px] font-mono">{(b.issued||[]).length} / <span className="text-emerald-600">{(b.cleared||[]).length}</span> / <span className="text-red-600">{(b.stopped||[]).length}</span> / <span className="text-amber-600">{(b.stale||[]).length}</span></td>
                <td className="p-2 text-right"><Button size="sm" variant="outline" onClick={() => nextNo(b.id)} className="rounded-sm h-7 text-[11px]" data-testid={`cb-next-${b.id}`}>Issue next</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="p-4 rounded-sm border">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">// Mark status</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Select value={statusForm.cb_id} onValueChange={v => setStatusForm({...statusForm, cb_id: v})}>
            <SelectTrigger className="rounded-sm h-10" data-testid="cb-status-picker"><SelectValue placeholder="Book" /></SelectTrigger>
            <SelectContent>{books.map(b => <SelectItem key={b.id} value={b.id}>{b.bank_key} {b.series_from}-{b.series_to}</SelectItem>)}</SelectContent>
          </Select>
          <Input placeholder="Cheque #" value={statusForm.cheque_number} onChange={e => setStatusForm({...statusForm, cheque_number: Number(e.target.value)})} className="rounded-sm h-10 text-right tabular-nums" data-testid="cb-status-num" />
          <Select value={statusForm.status} onValueChange={v => setStatusForm({...statusForm, status: v})}>
            <SelectTrigger className="rounded-sm h-10"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="cleared">Cleared</SelectItem><SelectItem value="stopped">Stopped</SelectItem><SelectItem value="stale">Stale</SelectItem></SelectContent>
          </Select>
          <Input placeholder="Reason (optional)" value={statusForm.reason} onChange={e => setStatusForm({...statusForm, reason: e.target.value})} className="rounded-sm h-10" />
          <Button onClick={setStatus} className="rounded-sm bg-accent hover:bg-accent/90 text-white h-10" data-testid="cb-status-save">Update</Button>
        </div>
      </Card>
    </div>
  );
}
